/**
 * ========================
 * DOMAIN PROVIDERS INDEX
 * ========================
 *
 * Auto-registers all menu providers via side effects.
 *
 * IMPORTANT:
 * This file is imported in main.tsx BEFORE React mounts.
 * The import triggers side-effect registration of all providers.
 *
 * USAGE:
 * In main.tsx, add at the top:
 * ```typescript
 * import './domain/providers'; // Auto-registers all menu providers
 * ```
 */

import { registerMenuProvider, registerGlobalMenuProvider } from '../DomainMenuRegistry';

// Import all providers
import { globalMenuProvider } from './globalMenu';
import { tasksMenuProvider } from './tasksMenu';
import { projectsMenuProvider } from './projectsMenu';
import { membersMenuProvider } from './membersMenu';
import { procurementsMenuProvider } from './procurementsMenu';
import { meetingsMenuProvider } from './meetingsMenu';
import { campusReportMenuProvider } from './campusReportMenu';
import { demoMenuProvider } from './demoMenu';
import { locationsMenuProvider } from './locationsMenu';
import { eventsMenuProvider } from './eventsMenu';
import { departmentsMenuProvider } from './departmentsMenu';
import { feedbackMenuProvider } from './feedbackMenu';
import { resourcesMenuProvider } from './resourcesMenu';
import { socialMediaMenuProvider } from './socialMediaMenu';
import { anatomyMenuProvider } from './anatomyMenu';
import { vendorsMenuProvider } from './vendorsMenu';
import { storeMenuProvider } from './storeMenu';
import { announcementsMenuProvider } from './announcementsMenu';
import { bookingsMenuProvider } from './bookingsMenu';
import { budgetsMenuProvider } from './budgetsMenu';
import { formsMenuProvider } from './formsMenu';
import { ridesMenuProvider } from './ridesMenu';
import { proceduresMenuProvider } from './proceduresMenu';
import { callCentreMenuProvider } from './callCentreMenu';
import { salvationMenuProvider } from './salvationMenu';
import { assetmanagementMenuProvider } from './assetmanagementMenu';
import { qrCodesMenuProvider } from './qrCodesMenu';
import { aiMenuProvider } from './aiMenu';
import { appCatalogueMenuProvider } from './appCatalogueMenu';
import { commsWhatsAppMenuProvider } from './commsWhatsAppMenu';
import { commsEmailMenuProvider } from './commsEmailMenu';
import { commsSmsMenuProvider } from './commsSmsMenu';
import { commsPushMenuProvider } from './commsPushMenu';

/**
 * Register all menu providers.
 *
 * Called automatically when this module is imported.
 * Registers both global and domain-specific providers.
 */
export function registerAllMenuProviders(): void {
  const startTime = performance.now();

  try {
    // Register global menu provider
    registerGlobalMenuProvider(globalMenuProvider);

    // Register domain menu providers
    registerMenuProvider(tasksMenuProvider);
    registerMenuProvider(projectsMenuProvider);
    registerMenuProvider(membersMenuProvider);
    registerMenuProvider(procurementsMenuProvider);
    registerMenuProvider(meetingsMenuProvider);
    registerMenuProvider(campusReportMenuProvider);
    registerMenuProvider(demoMenuProvider);
    registerMenuProvider(locationsMenuProvider);
    registerMenuProvider(eventsMenuProvider);
    registerMenuProvider(departmentsMenuProvider);
    registerMenuProvider(feedbackMenuProvider);
    registerMenuProvider(resourcesMenuProvider);
    registerMenuProvider(socialMediaMenuProvider);
    registerMenuProvider(anatomyMenuProvider);
    registerMenuProvider(vendorsMenuProvider);
    registerMenuProvider(storeMenuProvider);
    registerMenuProvider(announcementsMenuProvider);
    registerMenuProvider(bookingsMenuProvider);
    registerMenuProvider(budgetsMenuProvider);
    registerMenuProvider(formsMenuProvider);
    registerMenuProvider(ridesMenuProvider);
    registerMenuProvider(proceduresMenuProvider);
    registerMenuProvider(callCentreMenuProvider);
    registerMenuProvider(salvationMenuProvider);
    registerMenuProvider(assetmanagementMenuProvider);
    registerMenuProvider(qrCodesMenuProvider);
    registerMenuProvider(aiMenuProvider);
    registerMenuProvider(appCatalogueMenuProvider);
    registerMenuProvider(commsWhatsAppMenuProvider);
    registerMenuProvider(commsEmailMenuProvider);
    registerMenuProvider(commsSmsMenuProvider);
    registerMenuProvider(commsPushMenuProvider);

    const endTime = performance.now();
    const duration = (endTime - startTime).toFixed(2);

    console.log(
      `[MenuProviders] ✅ All menu providers registered successfully (${duration}ms)`
    );
  } catch (error) {
    console.error('[MenuProviders] ❌ Failed to register menu providers:', error);
    throw error; // Re-throw to prevent app from starting with broken menus
  }
}

/**
 * ========================
 * AUTO-REGISTRATION
 * ========================
 *
 * Side-effect: Register all providers when this module is imported.
 * This ensures providers are available before any React component mounts.
 */
registerAllMenuProviders();

/**
 * ========================
 * EXPORTS
 * ========================
 *
 * Export providers for testing and advanced use cases.
 * Most apps won't need these - they're registered automatically.
 */
export {
  globalMenuProvider,
  tasksMenuProvider,
  projectsMenuProvider,
  membersMenuProvider,
  procurementsMenuProvider,
  meetingsMenuProvider,
  campusReportMenuProvider,
  demoMenuProvider,
  locationsMenuProvider,
  eventsMenuProvider,
  departmentsMenuProvider,
  feedbackMenuProvider,
  resourcesMenuProvider,
  socialMediaMenuProvider,
  anatomyMenuProvider,
  vendorsMenuProvider,
  storeMenuProvider,
  announcementsMenuProvider,
  bookingsMenuProvider,
  budgetsMenuProvider,
  formsMenuProvider,
  ridesMenuProvider,
  proceduresMenuProvider,
  callCentreMenuProvider,
  salvationMenuProvider,
  assetmanagementMenuProvider,
  qrCodesMenuProvider,
  aiMenuProvider,
  appCatalogueMenuProvider,
  commsWhatsAppMenuProvider,
  commsEmailMenuProvider,
  commsSmsMenuProvider,
  commsPushMenuProvider,
};

/**
 * Export utilities for testing
 */
export { registerMenuProvider, registerGlobalMenuProvider } from '../DomainMenuRegistry';
